import React, { useState } from 'react';
import { MainLayout } from '../components/MainLayout';
import { ServiceCard } from '../components/catalog/ServiceCard';
import { ServiceForm } from '../components/catalog/ServiceForm';
import {
  useCatalogQuery,
  useCatalogCategoriesQuery,
  importDefaultServices,
  createService,
  updateService,
  deleteService,
  type CatalogFilters,
} from '../hooks/useCatalog';
import { useAuth } from '../contexts/AuthContext';
import type { DentalServiceCatalog } from '../types';

export function CatalogPage() {
  const { token, provider } = useAuth();
  const [filters, setFilters] = useState<CatalogFilters>({ active: true });
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<DentalServiceCatalog | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: services = [], isLoading, refetch } = useCatalogQuery(filters);
  const { data: categories = [] } = useCatalogCategoriesQuery();

  const canEdit = provider?.permissions?.edit || false;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setFilters((prev) => ({
      ...prev,
      search: query || undefined,
    }));
  };

  const handleCategoryFilter = (category: string) => {
    setFilters((prev) => ({
      ...prev,
      category: category === 'all' ? undefined : category,
    }));
  };

  const handleImportDefaults = async () => {
    if (!token) return;
    if (!confirm('Viltu flytja inn 23 sjálfgefnar CDT meðferðir? Þetta mun ekki yfirskrifa núverandi meðferðir.')) {
      return;
    }

    setIsImporting(true);
    try {
      await importDefaultServices(token);
      await refetch();
      alert('Sjálfgefnar meðferðir fluttar inn!');
    } catch (error) {
      console.error('Failed to import services:', error);
      alert('Villa kom upp við innflutning meðferða');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCreateNew = () => {
    setEditingService(null);
    setShowForm(true);
  };

  const handleEdit = (service: DentalServiceCatalog) => {
    setEditingService(service);
    setShowForm(true);
  };

  const handleDelete = async (service: DentalServiceCatalog) => {
    if (!token) return;
    if (!confirm(`Ertu viss um að þú viljir eyða ${service.name_is}?`)) {
      return;
    }

    try {
      await deleteService(service.id, token);
      await refetch();
    } catch (error) {
      console.error('Failed to delete service:', error);
      alert('Villa kom upp við að eyða meðferð');
    }
  };

  const handleSubmit = async (data: Partial<DentalServiceCatalog>) => {
    if (!token) return;

    setIsSubmitting(true);
    try {
      if (editingService) {
        await updateService(editingService.id, data, token);
      } else {
        await createService(data, token);
      }
      await refetch();
      setShowForm(false);
      setEditingService(null);
    } catch (error) {
      console.error('Failed to save service:', error);
      alert('Villa kom upp við að vista meðferð');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingService(null);
  };

  // Category info for display
  const categoryLabels: Record<string, string> = {
    diagnostic: 'Greining',
    preventive: 'Forvarnarmeðferð',
    restorative: 'Viðgerð',
    endodontics: 'Rótarmeðferð',
    periodontics: 'Tannholdslækning',
    oral_surgery: 'Munnholsaðgerð',
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Þjónustuskrá</h1>
          <p className="text-gray-600">Stjórna CDT kóðum og verðskrá</p>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingService ? 'Breyta meðferð' : 'Ný meðferð'}
              </h2>
              <ServiceForm
                service={editingService || undefined}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isLoading={isSubmitting}
              />
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Search */}
            <div className="flex-1 w-full sm:w-auto">
              <input
                type="search"
                placeholder="Leita að CDT kóða eða heiti..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleImportDefaults}
                disabled={!canEdit || isImporting}
                className="flex-1 sm:flex-none px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isImporting ? 'Flyt inn...' : 'Flytja inn sjálfgefnar'}
              </button>
              <button
                onClick={handleCreateNew}
                disabled={!canEdit}
                className="flex-1 sm:flex-none px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Ný meðferð
              </button>
            </div>
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleCategoryFilter('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                !filters.category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Allt ({categories.reduce((sum, cat) => sum + cat.count, 0)})
            </button>
            {categories.map((cat) => (
              <button
                key={cat.category}
                onClick={() => handleCategoryFilter(cat.category)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filters.category === cat.category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {categoryLabels[cat.category] || cat.category} ({cat.count})
              </button>
            ))}
          </div>
        </div>

        {/* Services Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Hleð meðferðum...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 mb-4">
              {searchQuery
                ? 'Engar meðferðir fundust'
                : 'Engar meðferðir í þjónustuskrá'}
            </p>
            {canEdit && !searchQuery && (
              <button
                onClick={handleImportDefaults}
                className="px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Flytja inn sjálfgefnar meðferðir
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={canEdit}
              />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
